/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useFlatSearchHistory } from '@/page/catalog-vehicles/hooks/use-flat-search-history';
import { useMarketplaceVehicleCatalogMgmtClient } from '@/rest/hooks/use-marketplace-vehicle-catalog-mgmt-client';
import { useUserId } from '@/rest/hooks/use-user-id';

import { SearchHistorySection } from './search-history-section.component';
import { useSearchDropdown } from '../search-dropdown.provider';
import { type UpdateSearchValueParams } from '../../search.component';

type SearchHistoryProps = {
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
};

export function SearchHistory({ updateSearchValue }: SearchHistoryProps): ReactNode {
  const { closeDropdown } = useSearchDropdown();

  const selectHistoryItem = useCallback(
    async (query: string): Promise<void> => {
      // TODO(randy): The searchHistoryId should be sent with the request
      await updateSearchValue({
        searchValue: query,
        searchType: 'LATEST_SEARCH',
        selectedFilter: null,
        autoSubmit: true,
      });
      closeDropdown();
    },
    [closeDropdown, updateSearchValue]
  );

  const queryClient = useQueryClient();
  const { client } = useMarketplaceVehicleCatalogMgmtClient();
  const userId = useUserId();

  useEffect(() => {
    return () => {
      if (userId != null) {
        queryClient.cancelQueries({
          queryKey: client.queryOptions('get', '/users/{userId}/catalog/search-history', {
            params: { path: { userId } },
          }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: client.queryOptions('get', '/users/{userId}/catalog/search-history', {
            params: { path: { userId } },
          }).queryKey,
        });
      }
    };
  }, [client, queryClient, userId]);

  const searchHistoryState = useFlatSearchHistory();
  const historyItems = useMemo(
    () => (searchHistoryState.status === 'success' ? searchHistoryState.data : []),
    [searchHistoryState.status, searchHistoryState.data]
  );

  return (
    <SearchHistorySection
      isPending={searchHistoryState.status === 'pending'}
      items={historyItems}
      onSelect={selectHistoryItem}
    />
  );
}
